'use client';

import { FAQSection, LinkButton } from '@homegrown/components';
import { AnalyticsEvent, useAnalytics } from '@homegrown/services';
import React from 'react';
import styles from './FoundingVendorPromoPage.module.css';

const FOUNDING_VENDOR_FORM_URL = process.env.NEXT_PUBLIC_FOUNDING_VENDOR_FORM_URL ?? '';

function FoundingVendorPromoPage() {
    const { trackEvent } = useAnalytics();
    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <div className={styles.badge}>Limited Offer</div>
                    <h1 className={styles.title}>
                        Get <span className={styles.highlight}>6 Months Free</span> on Homegrown
                    </h1>
                    <p className={styles.subtitle}>Be the first vendor within 50 miles to join Homegrown and sell online today.</p>
                    <div className={styles.ctaRow}>
                        <LinkButton targetLocation="/signup" text="Start Your Trial" />
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>How It Works</h2>
                <div className={styles.stepsGrid}>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>1</div>
                        <h3>Sign Up</h3>
                        <p>Create your free Homegrown account.</p>
                    </div>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>2</div>
                        <h3>Add a Product and Pickup Location</h3>
                        <p>Set up at least one product and where shoppers can pick it up.</p>
                    </div>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>✓</div>
                        <h3>Submit Your Claim</h3>
                        <p>Submit to claim your 6 months free as a founding vendor.</p>
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Why Be First?</h2>
                <div className={styles.benefitsGrid}>
                    <div className={styles.benefit}>
                        <h3>Top of Every Local Search</h3>
                        <p>When shoppers search your area online, you show up first. No competition for attention.</p>
                    </div>
                    <div className={styles.benefit}>
                        <h3>Reach Shoppers Anytime</h3>
                        <p>Your customers can browse and order from you 24/7 — not just when you&apos;re open.</p>
                    </div>
                    <div className={styles.benefit}>
                        <h3>Your Regulars Are Your Advantage</h3>
                        <p>Share your online store with customers who already know you. They keep ordering. New shoppers follow.</p>
                    </div>
                    <div className={styles.benefit}>
                        <h3>We Handle the Hard Stuff</h3>
                        <p>Sales tax, payments, and order management — covered. You focus on what you grow, make, or sell.</p>
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>What You Get</h2>
                <div className={styles.featureList}>
                    <div className={styles.featureItem}>Online storefront — your products, your brand</div>
                    <div className={styles.featureItem}>Listed on Homegrown&apos;s marketplace — shoppers discover you</div>
                    <div className={styles.featureItem}>Online pre-orders and pickup scheduling</div>
                    <div className={styles.featureItem}>Automated sales tax remittance</div>
                    <div className={styles.featureItem}>All for $0/month for your first 6 months</div>
                </div>
            </section>

            <section className={`${styles.section} ${styles.faqSection}`}>
                <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
                <FAQSection
                    border={false}
                    faqs={[
                        {
                            question: 'What does "6 months free" actually include?',
                            answer: 'Your monthly subscription fee is waived for 6 months. Credit card processing fees still apply during the promotional period.',
                        },
                        {
                            question: 'Who qualifies as a founding vendor?',
                            answer: 'The first vendor to complete signup within a 50-mile radius qualifies as a founding vendor. You must create an account, add at least one product and a pickup location, and submit your claim.',
                        },
                        {
                            question: 'When does the 6 months start?',
                            answer: 'Your 6 free months begin once our team reviews and approves your claim. You\u2019ll receive an email when your promotional period is active.',
                        },
                        {
                            question: 'What happens after the 6 months end?',
                            answer: 'After the promotional period, standard pricing applies. You\u2019ll be notified before the transition so there are no surprises.',
                        },
                        {
                            question: 'Do I need to list a certain number of products to qualify?',
                            answer: 'No — you just need at least one product and one pickup location to be eligible. List as many or as few as you like.',
                        },
                        {
                            question: 'Can I cancel anytime?',
                            answer: 'Yes. You can cancel your Homegrown account at any time, including during the promotional period.',
                        },
                        {
                            question: 'How do I submit my claim?',
                            answer: 'After you\u2019ve signed up, added a product, and set up a pickup location, use the "Claim your offer" link on this page. Our team will review it and email you once approved.',
                        },
                    ]}
                />
            </section>

            <section className={styles.ctaSection}>
                <h2 className={styles.sectionTitle}>Ready to Get Started?</h2>
                <p className={styles.ctaText}>
                    Sign up, add a product and pickup location, then submit your claim. We&apos;ll email you when your 6 free months are active.
                </p>
                <div className={styles.ctaButtons}>
                    <LinkButton targetLocation="/signup" text="Start Your Free Trial" />
                    {FOUNDING_VENDOR_FORM_URL !== '' && (
                        <button
                            type="button"
                            className={styles.formLink}
                            onClick={() => {
                                trackEvent(AnalyticsEvent.FOUNDING_PROMO_FORM_CLICKED, { surface: 'promo_page' });
                                window.open(FOUNDING_VENDOR_FORM_URL, '_blank');
                            }}
                        >
                            Already signed up? Claim your offer.
                        </button>
                    )}
                </div>
            </section>

            <section className={styles.fineprint}>
                <p>
                    Offer available to the first vendor to complete signup within a 50-mile radius. Must create an account, add at least one product,
                    and set up a pickup location to qualify. Your claim will be manually reviewed and you&apos;ll be emailed when approved. Credit
                    card processing fees and a $1 per order fee apply. After the 6-month promotional period, standard pricing applies. Cancel anytime.
                </p>
            </section>
        </div>
    );
}

export default FoundingVendorPromoPage;
